// pages/stock/StockAuditDialog.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Divider,
    Tooltip,
    TablePagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    SwapHoriz as TransferIcon,
    RemoveCircle as ConsumeIcon,
    Replay as ReverseIcon,
    Inventory as CountIcon,
    History as HistoryIcon,
    ArrowForward as ArrowIcon,
    TrendingDown as TrendingDownIcon,
    TrendingUp as TrendingUpIcon,
    ArrowDownward as ArrowDownIcon,
    ArrowUpward as ArrowUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { stockAPI } from '../../services/api';

// ── Action config ─────────────────────────────────────────────────────────
const ACTION_CONFIG = {
    created: {
        icon: <AddIcon />,
        color: 'success',
    },
    updated: {
        icon: <EditIcon />,
        color: 'primary',
    },
    deleted: {
        icon: <DeleteIcon />,
        color: 'error',
    },
    quantity_updated: {
        icon: <EditIcon />,
        color: 'info',
    },
    transfer_outgoing: {
        icon: <TrendingDownIcon />,
        color: 'warning',
    },
    transfer_incoming: {
        icon: <TrendingUpIcon />,
        color: 'success',
    },
    consumed: {
        icon: <ConsumeIcon />,
        color: 'error',
    },
    consumption_reversed: {
        icon: <ReverseIcon />,
        color: 'info',
    },
    physical_count: {
        icon: <CountIcon />,
        color: 'secondary',
    },
};

const ALL_ACTIONS = Object.keys(ACTION_CONFIG);

const StockAuditDialog = ({ open, onClose, stockItem }) => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(25);
    const [total, setTotal] = useState(0);
    const [actionFilter, setActionFilter] = useState('');

    const fetchAuditLogs = useCallback(async () => {
        if (!stockItem?._id) return;
        setLoading(true);
        try {
            const params = { page: page + 1, limit };
            if (actionFilter) params.action = actionFilter;

            const res = await stockAPI.getAuditTrail(stockItem._id, params);
            setLogs(res.data?.data || []);
            setTotal(res.data?.pagination?.total || 0);
        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                t('stock.audit.fetchError', 'Failed to fetch audit trail')
            );
        } finally {
            setLoading(false);
        }
    }, [stockItem?._id, page, limit, actionFilter, t]);

    useEffect(() => {
        if (open && stockItem?._id) fetchAuditLogs();
    }, [open, fetchAuditLogs]);

    useEffect(() => {
        if (!open) {
            setLogs([]);
            setPage(0);
            setActionFilter('');
        }
    }, [open]);

    const formatDateTime = (date) => {
        if (!date) return '-';
        return format(new Date(date), 'dd/MM/yyyy HH:mm:ss');
    };

    const getActionLabel = (action) => {
        const labels = {
            created: t('stock.audit.created', 'Created'),
            updated: t('stock.audit.updated', 'Updated'),
            deleted: t('stock.audit.deleted', 'Deleted'),
            quantity_updated: t('stock.audit.quantityUpdated', 'Qty Updated'),
            transfer_outgoing: t('stock.audit.transferOut', 'Transfer Out'),
            transfer_incoming: t('stock.audit.transferIn', 'Transfer In'),
            consumed: t('stock.audit.consumed', 'Consumed'),
            consumption_reversed: t('stock.audit.reversed', 'Reversed'),
            physical_count: t('stock.audit.physicalCount', 'Physical Count'),
        };
        return labels[action] || action;
    };

    const getFieldLabel = (field) => {
        const labels = {
            name: t('stock.name', 'Name'),
            unit: t('stock.unit', 'Unit'),
            price: t('stock.price', 'Price'),
            minimumThreshold: t('stock.minimumThreshold', 'Min. Threshold'),
            category: t('stock.category', 'Category'),
            validUntil: t('stock.validUntil', 'Valid Until'),
            isActive: t('common.active', 'Active'),
        };
        return labels[field] || field;
    };

    /**
     * Resolve a change value to a displayable string.
     * Handles objects like { _id, name }, plain IDs resolved via
     * snapshot helpers, dates, booleans, numbers, etc.
     */
    const resolveDisplayValue = (field, value, log) => {
        if (value === null || value === undefined) return '-';

        // If the value is an object (e.g. populated category)
        if (typeof value === 'object' && !Array.isArray(value)) {
            // Prefer .name, then .username, then .label, then fallback to _id
            return value.name || value.username || value.label || value._id || JSON.stringify(value);
        }

        // For category field: if it's a plain ID string, try to resolve from snapshot
        if (field === 'category' && typeof value === 'string') {
            // Check if snapshot has categoryName and the ID matches
            if (log?.snapshot?.category === value && log?.snapshot?.categoryName) {
                return log.snapshot.categoryName;
            }
            // Otherwise return the raw ID (truncated)
            return value.length > 12 ? `${value.slice(0, 8)}…` : value;
        }

        // Date fields
        if (field === 'validUntil' && value) {
            try {
                return format(new Date(value), 'dd/MM/yyyy');
            } catch {
                return String(value);
            }
        }

        // Numeric fields
        if (field === 'price' || field === 'minimumThreshold') {
            return Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        }

        // Boolean fields
        if (field === 'isActive') {
            return value ? t('common.yes', 'Yes') : t('common.no', 'No');
        }

        return String(value);
    };

    /**
     * Find branch name from branchesLookup array.
     */
    const getBranchName = (log, branchId) => {
        if (!branchId || !log.branchesLookup) return null;
        const found = log.branchesLookup.find(
            (b) => String(b._id) === String(branchId)
        );
        return found?.name || null;
    };

    // ── Render: field-level changes ───────────────────────────────────
    const renderFieldChanges = (log) => {
        if (!log.changes || log.changes.length === 0) return null;

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {t('stock.audit.fieldChanges', 'Field Changes')}:
                </Typography>
                {log.changes.map((change, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 0.5,
                            pl: 1,
                        }}
                    >
                        <Chip
                            label={getFieldLabel(change.field)}
                            size="small"
                            variant="outlined"
                            sx={{ minWidth: 80 }}
                        />
                        <Tooltip title={resolveDisplayValue(change.field, change.oldValue, log)}>
                            <Typography
                                variant="caption"
                                sx={{
                                    textDecoration: 'line-through',
                                    color: 'error.main',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {resolveDisplayValue(change.field, change.oldValue, log)}
                            </Typography>
                        </Tooltip>
                        <ArrowIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Tooltip title={resolveDisplayValue(change.field, change.newValue, log)}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'success.main',
                                    fontWeight: 'bold',
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {resolveDisplayValue(change.field, change.newValue, log)}
                            </Typography>
                        </Tooltip>
                    </Box>
                ))}
            </Box>
        );
    };

    // ── Render: quantity changes ───────────────────────────────────────
    const renderQuantityChanges = (log) => {
        if (!log.quantityChanges || log.quantityChanges.length === 0) return null;

        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {t('stock.audit.quantityChanges', 'Quantity Changes')}:
                </Typography>
                {log.quantityChanges.map((qc, idx) => {
                    const branchName = getBranchName(log, qc.branchId) || qc.branchId;
                    const isPositive = qc.difference > 0;

                    return (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                                pl: 1,
                            }}
                        >
                            <Chip
                                label={branchName}
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 80 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {qc.previousQuantity}
                            </Typography>
                            <ArrowIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight="bold">
                                {qc.newQuantity}
                            </Typography>
                            <Chip
                                icon={isPositive ? <ArrowUpIcon sx={{ fontSize: 14 }} /> : <ArrowDownIcon sx={{ fontSize: 14 }} />}
                                label={`${isPositive ? '+' : ''}${qc.difference}`}
                                size="small"
                                color={isPositive ? 'success' : 'error'}
                                variant="outlined"
                                sx={{ minWidth: 60 }}
                            />
                        </Box>
                    );
                })}
            </Box>
        );
    };

    // ── Render: transfer details ──────────────────────────────────────
    const renderTransferDetails = (log) => {
        const td = log.transferDetails;
        if (!td || (!td.sourceBranchId && !td.destinationBranchId)) return null;

        const sourceName = td.sourceBranchName || getBranchName(log, td.sourceBranchId) || td.sourceBranchId || t('stock.audit.central', 'Central');
        const destName = td.destinationBranchName || getBranchName(log, td.destinationBranchId) || td.destinationBranchId;

        return (
            <Box
                sx={{
                    mt: 1,
                    p: 1,
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                }}
            >
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {t('stock.audit.transferInfo', 'Transfer Info')}:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip label={sourceName} size="small" color="warning" variant="outlined" />
                    <ArrowIcon sx={{ fontSize: 14 }} />
                    <Chip label={destName} size="small" color="success" variant="outlined" />
                    <Typography variant="caption">
                        ×{td.quantity}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} mt={0.5}>
                    {td.sourceWac > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {t('stock.audit.sourceWac', 'Source WAC')}: {td.sourceWac?.toFixed(2)}
                        </Typography>
                    )}
                    {td.newDestinationWac > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {t('stock.audit.newWac', 'New WAC')}: {td.newDestinationWac?.toFixed(2)}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    // ── Render: consumption details ───────────────────────────────────
    const renderConsumptionDetails = (log) => {
        const cd = log.consumptionDetails;
        if (!cd || !cd.branchId) return null;

        const branchName = getBranchName(log, cd.branchId) || cd.branchId;

        return (
            <Box
                sx={{
                    mt: 1,
                    p: 1,
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                }}
            >
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {log.action === 'consumption_reversed'
                        ? t('stock.audit.reversalInfo', 'Reversal Info')
                        : t('stock.audit.consumptionInfo', 'Consumption Info')}:
                </Typography>
                <Stack direction="row" spacing={2} mt={0.5} flexWrap="wrap">
                    <Typography variant="caption">
                        <strong>{t('common.branch', 'Branch')}:</strong> {branchName}
                    </Typography>
                    <Typography variant="caption">
                        <strong>{t('stock.quantity', 'Qty')}:</strong> {cd.quantity}
                    </Typography>
                    {cd.wacAtConsumption > 0 && (
                        <Typography variant="caption">
                            <strong>WAC:</strong> {cd.wacAtConsumption?.toFixed(2)}
                        </Typography>
                    )}
                    {cd.totalCost > 0 && (
                        <Typography variant="caption">
                            <strong>{t('stock.audit.totalCost', 'Cost')}:</strong> {cd.totalCost?.toFixed(2)}
                        </Typography>
                    )}
                </Stack>
            </Box>
        );
    };

    // ── Render: snapshot summary ──────────────────────────────────────
    const renderSnapshot = (log) => {
        if (!log.snapshot) return null;

        return (
            <Box
                sx={{
                    mt: 1,
                    p: 1,
                    borderRadius: 1,
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                }}
            >
                <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ mr: 1 }}>
                    {t('stock.audit.snapshot', 'Snapshot')}:
                </Typography>
                {log.snapshot.name && (
                    <Chip
                        label={`${t('stock.name', 'Name')}: ${log.snapshot.name}`}
                        size="small"
                        variant="outlined"
                    />
                )}
                {log.snapshot.price != null && (
                    <Chip
                        label={`${t('stock.price', 'Price')}: ${log.snapshot.price?.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        })}`}
                        size="small"
                        variant="outlined"
                    />
                )}
                {log.snapshot.categoryName && (
                    <Chip
                        label={`${t('stock.category', 'Category')}: ${log.snapshot.categoryName}`}
                        size="small"
                        variant="outlined"
                    />
                )}
                {log.snapshot.unit && (
                    <Chip
                        label={`${t('stock.unit', 'Unit')}: ${log.snapshot.unit}`}
                        size="small"
                        variant="outlined"
                    />
                )}
                {log.snapshot.validUntil && (
                    <Chip
                        label={`${t('stock.validUntil', 'Valid Until')}: ${format(new Date(log.snapshot.validUntil), 'dd/MM/yyyy')}`}
                        size="small"
                        variant="outlined"
                    />
                )}
                {log.snapshot.isActive != null && (
                    <Chip
                        label={log.snapshot.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                        size="small"
                        color={log.snapshot.isActive ? 'success' : 'default'}
                        variant="outlined"
                    />
                )}
            </Box>
        );
    };

    if (!stockItem) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                {t('stock.audit.title', 'Stock Audit Trail')}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    — {stockItem.name}
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* ── Filter ── */}
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>
                            {t('stock.audit.filterByAction', 'Filter by Action')}
                        </InputLabel>
                        <Select
                            value={actionFilter}
                            onChange={(e) => {
                                setActionFilter(e.target.value);
                                setPage(0);
                            }}
                            label={t('stock.audit.filterByAction', 'Filter by Action')}
                        >
                            <MenuItem value="">
                                {t('common.all', 'All')}
                            </MenuItem>
                            {ALL_ACTIONS.map((action) => (
                                <MenuItem key={action} value={action}>
                                    {getActionLabel(action)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Chip
                        label={`${total} ${t('stock.audit.entries', 'entries')}`}
                        size="small"
                        variant="outlined"
                    />
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* ── Content ── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : logs.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography color="text.secondary">
                            {t('stock.audit.noLogs', 'No audit logs found')}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                        {logs.map((log) => {
                            const config = ACTION_CONFIG[log.action] || {
                                icon: <HistoryIcon />,
                                color: 'default',
                            };

                            return (
                                <Paper
                                    key={log._id}
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        mb: 1.5,
                                        borderLeft: 4,
                                        borderLeftColor: `${config.color}.main`,
                                    }}
                                >
                                    {/* Header */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <Tooltip title={getActionLabel(log.action)}>
                                                <Box
                                                    sx={{
                                                        color: `${config.color}.main`,
                                                        display: 'flex',
                                                    }}
                                                >
                                                    {config.icon}
                                                </Box>
                                            </Tooltip>
                                            <Box>
                                                <Chip
                                                    label={getActionLabel(log.action)}
                                                    size="small"
                                                    color={config.color}
                                                    variant="filled"
                                                />
                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                    {t('stock.audit.by', 'by')}{' '}
                                                    <strong>
                                                        {log.performedByUser?.name ||
                                                            log.performedByUser?.username ||
                                                            '-'}
                                                    </strong>
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDateTime(log.createdAt)}
                                        </Typography>
                                    </Box>

                                    {/* Field Changes */}
                                    {renderFieldChanges(log)}

                                    {/* Quantity Changes */}
                                    {renderQuantityChanges(log)}

                                    {/* Transfer Details */}
                                    {renderTransferDetails(log)}

                                    {/* Consumption Details */}
                                    {renderConsumptionDetails(log)}

                                    {/* Metadata reason */}
                                    {log.metadata?.reason && (
                                        <Box sx={{ mt: 1, pl: 1 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {t('stock.audit.reason', 'Reason')}:{' '}
                                            </Typography>
                                            <Typography variant="caption" fontStyle="italic">
                                                {log.metadata.reason}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Snapshot */}
                                    {renderSnapshot(log)}
                                </Paper>
                            );
                        })}
                    </Box>
                )}

                {/* Pagination */}
                {total > 0 && (
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={limit}
                        onRowsPerPageChange={(e) => {
                            setLimit(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                        labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                    />
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose}>
                    {t('common.close', 'Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StockAuditDialog;